import { useNavigate } from 'react-router-dom'
import {
  Block,
  BlockTitle,
  List,
  ListItem,
} from 'konsta/react'
import {
  DoorOpen,
  Plus,
  History,
  BarChart3,
  Trophy,
  Users,
  CircleDot,
  Clock,
  Wallet,
  ChevronRight,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { formatCurrency } from '@/lib/utils'

const quickActions = [
  { label: '加入房間', icon: DoorOpen, color: '#007AFF', path: '/rooms' },
  { label: '創建房間', icon: Plus, color: '#34C759', path: '/create-room' },
  { label: '手牌歷史', icon: History, color: '#FF9500', path: '/history' },
  { label: '統計', icon: BarChart3, color: '#AF52DE', path: '/stats' },
]

const activeRooms = [
  { id: '1', name: '新手桌 #1', blinds: '1/2', players: '6/9', status: '進行中' },
  { id: '2', name: '中級桌 #3', blinds: '5/10', players: '4/9', status: '等待中' },
  { id: '3', name: '高額桌 VIP', blinds: '25/50', players: '8/9', status: '進行中' },
]

const recentActivity = [
  { id: '1', text: '在新手桌 #1 贏得底池', amount: 4500, time: '5 分鐘前' },
  { id: '2', text: '加入中級桌 #3', amount: 0, time: '1 小時前' },
  { id: '3', text: '存款成功', amount: 50000, time: '3 小時前' },
]

export function HomeScreen() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  return (
    <div className="pb-4">
      {/* Welcome Section */}
      <Block className="!px-4 !mt-4 !mb-0">
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[13px] text-[#8E8E93]">歡迎回來</p>
              <h2 className="text-xl font-bold text-black mt-0.5">
                {user?.name ?? '玩家'}
              </h2>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-[#8E8E93] uppercase tracking-wide">餘額</p>
              <p className="text-xl font-bold text-[#007AFF] mt-0.5">
                {formatCurrency(user?.balance ?? 0)}
              </p>
            </div>
          </div>
        </div>
      </Block>

      {/* Quick Actions - 2x2 grid */}
      <BlockTitle className="!mb-2">快速操作</BlockTitle>
      <Block className="!px-4 !mt-0 !mb-0">
        <div className="grid grid-cols-2 gap-2.5">
          {quickActions.map((action) => {
            const Icon = action.icon
            return (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 flex flex-col items-center gap-2.5 active:scale-[0.97] transition-transform"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${action.color}15` }}
                >
                  <Icon className="w-5.5 h-5.5" style={{ color: action.color }} />
                </div>
                <span className="text-[13px] font-semibold text-black">{action.label}</span>
              </button>
            )
          })}
        </div>
      </Block>

      {/* Jackpot Banner */}
      <Block className="!px-4 !mt-3 !mb-0">
        <div
          className="rounded-2xl p-4 flex items-center gap-4 cursor-pointer active:opacity-90 transition-opacity"
          style={{ background: 'linear-gradient(135deg, #FF9500, #FF3B30)' }}
          onClick={() => navigate('/rooms')}
        >
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white/80 text-xs font-medium">累積獎池</p>
            <p className="text-white text-2xl font-bold">$88,888</p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/40" />
        </div>
      </Block>

      {/* Active Rooms */}
      <BlockTitle className="flex items-center justify-between !pr-4 !mb-2">
        <span>活躍房間</span>
        <button
          onClick={() => navigate('/rooms')}
          className="text-[13px] font-medium text-[#007AFF]"
        >
          查看全部
        </button>
      </BlockTitle>
      <List strongIos insetIos className="!mt-0">
        {activeRooms.map((room) => (
          <ListItem
            key={room.id}
            link
            chevronIos
            onClick={() => navigate(`/room/${room.id}`)}
            title={<span className="font-semibold">{room.name}</span>}
            subtitle={`盲注 $${room.blinds}`}
            after={
              <span
                className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  room.status === '進行中'
                    ? 'bg-[#34C759]/10 text-[#34C759]'
                    : 'bg-[#FF9500]/10 text-[#FF9500]'
                }`}
              >
                {room.status}
              </span>
            }
            media={
              <div className="w-10 h-10 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#007AFF]" />
              </div>
            }
            text={
              <span className="flex items-center gap-1 text-[#8E8E93]">
                <CircleDot className="w-3 h-3" />
                {room.players} 玩家
              </span>
            }
          />
        ))}
      </List>

      {/* Recent Activity */}
      <BlockTitle className="!mb-2">最近動態</BlockTitle>
      <List strongIos insetIos className="!mt-0">
        {recentActivity.map((activity) => (
          <ListItem
            key={activity.id}
            title={<span className="text-[14px]">{activity.text}</span>}
            after={
              activity.amount > 0 ? (
                <span className="text-[13px] font-semibold text-[#34C759]">
                  +{formatCurrency(activity.amount)}
                </span>
              ) : null
            }
            subtitle={
              <span className="flex items-center gap-1 text-[#8E8E93] text-[12px]">
                <Clock className="w-3 h-3" />
                {activity.time}
              </span>
            }
            media={
              <div className="w-10 h-10 rounded-xl bg-[#F2F2F7] flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#8E8E93]" />
              </div>
            }
          />
        ))}
      </List>
    </div>
  )
}
